if (!customElements.get('puja-booking-detail')) {
  class PujaBookingDetail extends HTMLElement {
    constructor() {
      super();
      this.steps = ['muhurat', 'participants', 'language', 'addons', 'phone', 'cart', 'address', 'address-form', 'review'];
      this.history = [];
      this.currentStep = 'muhurat';
      this.lastFocusedElement = null;
      this.state = {
        bookingType: 'Single',
        participants: [],
        familyCount: 3,
        phone: '',
        muhuratType: 'selected',
        dateTime: '',
        language: '',
        addonId: null,
        addonTitle: '',
        addonPrice: 0,
        address: null,
        coupon: ''
      };
      this.boundClick = this.handleClick.bind(this);
      this.boundChange = this.handleChange.bind(this);
      this.boundAddressSubmit = this.handleAddressSubmit.bind(this);
      this.boundKeydown = this.handleKeydown.bind(this);
      this.boundExtraSubmit = this.handleExtraSubmit.bind(this);
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.modal = this.querySelector('[data-booking-modal]');
      if (!this.modal) return;

      this.dialog = this.querySelector('.puja-booking-modal__dialog');
      this.backButton = this.querySelector('[data-booking-back]');
      this.notice = this.querySelector('[data-booking-notice]');
      this.progressBar = this.querySelector('[data-progress-bar]');
      this.addressForm = this.querySelector('[data-address-form]');

      this.addEventListener('click', this.boundClick);
      this.addEventListener('change', this.boundChange);
      this.addEventListener('input', this.boundChange);
      this.addressForm?.addEventListener('submit', this.boundAddressSubmit);
      this.querySelector('[data-participants-form]')?.addEventListener('submit', this.boundExtraSubmit);
      this.querySelector('[data-booking-phone-form]')?.addEventListener('submit', this.boundExtraSubmit);
      document.addEventListener('keydown', this.boundKeydown);

      this.setDateMinimum();
      this.renderLocalAddresses();
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.boundKeydown);
    }

    handleClick(event) {
      const packageToggle = event.target.closest('[data-puja-package-toggle]');
      if (packageToggle) {
        const panel = this.querySelector(`#${CSS.escape(packageToggle.getAttribute('aria-controls'))}`);
        if (!panel) return;
        const expanded = packageToggle.getAttribute('aria-expanded') !== 'true';
        panel.hidden = !expanded;
        packageToggle.setAttribute('aria-expanded', String(expanded));
        packageToggle.querySelector('[data-puja-package-toggle-label]').textContent = expanded
          ? packageToggle.dataset.lessLabel : packageToggle.dataset.moreLabel;
        if (expanded && window.matchMedia('(max-width: 749px)').matches) {
          panel.scrollIntoView({ block: 'start', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
        }
        return;
      }

      const openButton = event.target.closest('[data-open-booking]');
      if (openButton) {
        this.openModal(openButton);
        return;
      }

      if (event.target.closest('[data-close-booking]')) {
        this.closeModal();
        return;
      }

      if (event.target.closest('[data-booking-back]')) {
        this.goBack();
        return;
      }

      const nextButton = event.target.closest('[data-next-step]');
      if (nextButton) {
        this.advanceTo(nextButton.dataset.nextStep);
        return;
      }

      if (event.target.closest('[data-address-continue]')) {
        this.continueFromAddress();
        return;
      }

      if (event.target.closest('[data-apply-coupon]')) {
        this.captureCoupon();
        return;
      }

      const payButton = event.target.closest('[data-pay-now]');
      if (payButton) this.payNow(payButton);
    }

    handleChange(event) {
      if (event.target.matches('[data-family-count]')) {
        this.saveParticipantDraft();
        const count = Number(event.target.value);
        if (Number.isInteger(count) && count >= 2 && count <= 50) {
          this.state.familyCount = count;
          this.renderParticipants();
        }
      }

      if (event.target.matches('input[name^="muhurat-"]')) {
        this.state.muhuratType = event.target.value;
        const dateInput = this.querySelector('[data-booking-datetime]');
        if (dateInput) {
          dateInput.disabled = event.target.value === 'pandit';
          if (!dateInput.disabled) dateInput.focus();
        }
      }
    }

    openModal(trigger) {
      this.lastFocusedElement = trigger || document.activeElement;
      this.configureBookingFromTrigger(trigger);
      this.resetBooking();
      this.modal.hidden = false;
      document.body.classList.add('puja-booking-is-open');
      this.dialog.focus({ preventScroll: true });
    }

    configureBookingFromTrigger(trigger) {
      if (!trigger?.dataset.bookingVariantId) return;

      this.dataset.variantId = trigger.dataset.bookingVariantId;
      this.dataset.variantPrice = trigger.dataset.bookingVariantPrice || '0';
      this.dataset.variantAvailable = trigger.dataset.bookingVariantAvailable || 'true';
      this.dataset.productTitle = trigger.dataset.bookingProductTitle || 'Puja service';

      this.modal.querySelectorAll('[data-booking-product-title-output]').forEach((element) => {
        element.textContent = this.dataset.productTitle;
      });

      const dynamicAddons = this.querySelector('[data-booking-dynamic-addons]');
      if (dynamicAddons) {
        dynamicAddons.replaceChildren();
        const templateId = trigger.dataset.bookingAddonsTemplate;
        const template = templateId ? this.querySelector(`#${CSS.escape(templateId)}`) : null;
        if (template?.content) dynamicAddons.append(template.content.cloneNode(true));
        dynamicAddons.querySelectorAll('input[type="radio"]').forEach((input) => {
          input.name = `addon-${this.dataset.sectionId}`;
        });
      }
    }

    closeModal() {
      this.modal.hidden = true;
      document.body.classList.remove('puja-booking-is-open');
      this.clearNotice();
      if (this.lastFocusedElement && typeof this.lastFocusedElement.focus === 'function') {
        this.lastFocusedElement.focus({ preventScroll: true });
      }
    }

    resetBooking() {
      this.history = [];
      this.state = {
        bookingType: 'Single',
        participants: [],
        familyCount: 3,
        phone: '',
        muhuratType: 'selected',
        dateTime: '',
        language: '',
        addonId: null,
        addonTitle: '',
        addonPrice: 0,
        address: null,
        coupon: ''
      };

      const phoneInput = this.querySelector('[data-booking-phone]');
      if (phoneInput) phoneInput.value = '';
      const countInput = this.querySelector('[data-family-count]');
      if (countInput) countInput.value = '3';
      this.renderParticipants();

      const defaultMuhurat = this.querySelector('input[name^="muhurat-"][value="selected"]');
      const firstLanguage = this.querySelector('input[name^="language-"]:not(:disabled)');
      const noAddon = this.querySelector('input[name^="addon-"][value="none"]');
      if (defaultMuhurat) defaultMuhurat.checked = true;
      if (firstLanguage) firstLanguage.checked = true;
      if (noAddon) noAddon.checked = true;

      const dateInput = this.querySelector('[data-booking-datetime]');
      if (dateInput) {
        dateInput.disabled = false;
        dateInput.value = '';
      }

      this.querySelectorAll('input[name^="address-"]').forEach((input) => { input.checked = false; });
      const couponInput = this.querySelector('[data-coupon-code]');
      if (couponInput) couponInput.value = '';
      const submitButton = this.querySelector('[data-pay-now]');
      if (submitButton) {
        submitButton.disabled = false;
        submitButton.classList.remove('is-loading');
      }
      this.setStep('muhurat', false);
      this.clearNotice();
      this.renderLocalAddresses();
    }

    advanceTo(nextStep) {
      this.clearNotice();
      if (!this.captureCurrentStep()) return;

      if (this.extendedBooking && this.currentStep === 'muhurat' && this.state.bookingType !== 'Single') nextStep = 'participants';
      if (nextStep === 'cart') this.updateCartSummary();
      if (nextStep === 'address') this.renderLocalAddresses();
      this.setStep(nextStep);
    }

    captureCurrentStep() {
      if (this.currentStep === 'muhurat') {
        const selectedType = this.querySelector('input[name^="muhurat-"]:checked');
        const dateInput = this.querySelector('[data-booking-datetime]');
        this.state.muhuratType = selectedType?.value || 'selected';

        if (this.state.muhuratType === 'selected') {
          if (!dateInput?.value) {
            this.showNotice('Please select a preferred date and time.');
            dateInput?.focus();
            return false;
          }

          const selectedDate = new Date(dateInput.value);
          if (Number.isNaN(selectedDate.getTime()) || selectedDate.getTime() < Date.now() - 60000) {
            this.showNotice('Please select a future date and time.');
            dateInput.focus();
            return false;
          }
          this.state.dateTime = dateInput.value;
        } else {
          this.state.dateTime = '';
        }
      }

      if (this.currentStep === 'participants' && !this.captureParticipants()) return false;
      if (this.currentStep === 'phone' && !this.capturePhone()) return false;

      if (this.currentStep === 'language') {
        const language = this.querySelector('input[name^="language-"]:checked');
        if (!language) {
          this.showNotice('Please choose the Pandit’s preferred language.');
          return false;
        }
        this.state.language = language.value;
      }

      if (this.currentStep === 'addons') {
        const addon = this.querySelector('input[name^="addon-"]:checked');
        if (!addon) {
          this.showNotice('Please choose an add-on or select “No add-on”.');
          return false;
        }

        if (addon.value === 'none') {
          this.state.addonId = null;
          this.state.addonTitle = '';
          this.state.addonPrice = 0;
        } else {
          if (addon.disabled || !Number.isSafeInteger(Number(addon.value)) || Number(addon.value) <= 0) {
            this.showNotice('This add-on is currently unavailable. Please choose another option.');
            return false;
          }
          this.state.addonId = Number(addon.value);
          this.state.addonTitle = addon.dataset.addonTitle || 'Add-on';
          this.state.addonPrice = Number(addon.dataset.addonPrice || 0);
        }
      }

      return true;
    }

    setStep(stepName, addToHistory = true) {
      const nextPanel = this.querySelector(`[data-booking-step="${stepName}"]`);
      if (!nextPanel) return;

      if (addToHistory && this.currentStep !== stepName) this.history.push(this.currentStep);
      this.querySelectorAll('[data-booking-step]').forEach((panel) => {
        const active = panel === nextPanel;
        panel.hidden = !active;
        panel.classList.toggle('is-active', active);
      });

      this.currentStep = stepName;
      if (this.backButton) this.backButton.hidden = this.history.length === 0;

      const flowSteps = this.steps.filter((step) => {
        if (step === 'address-form') return false;
        if (step === 'phone') return this.extendedBooking;
        if (step === 'participants') return this.extendedBooking && this.state.bookingType !== 'Single';
        return true;
      });
      const progressStep = stepName === 'address-form' ? 'address' : stepName;
      const progressIndex = Math.max(0, flowSteps.indexOf(progressStep));
      if (this.progressBar) this.progressBar.style.width = `${((progressIndex + 1) / flowSteps.length) * 100}%`;

      const body = this.querySelector('.puja-booking-modal__body');
      if (body) body.scrollTop = 0;
      const heading = nextPanel.querySelector('h3');
      window.requestAnimationFrame(() => {
        heading?.setAttribute('tabindex', '-1');
        heading?.focus({ preventScroll: true });
      });
    }

    goBack() {
      const previous = this.history.pop();
      if (!previous) return;
      this.setStep(previous, false);
      this.clearNotice();
    }

    updateCartSummary() {
      const total = this.basePrice + this.state.addonPrice;
      const dateLabel = this.dateLabel;
      const addonLabel = this.state.addonTitle || this.noAddonLabel;

      this.setText('[data-summary-date]', dateLabel);
      this.setText('[data-summary-language]', this.state.language);
      this.setText('[data-summary-addon]', addonLabel);
      this.setText('[data-summary-total]', this.formatMoney(total));
      this.setText('[data-summary-subtotal]', this.formatMoney(total));
      this.updatePeopleSummary('summary');

      const breakdown = this.querySelector('[data-summary-breakdown]');
      if (breakdown) {
        breakdown.replaceChildren();
        const base = document.createElement('span');
        base.textContent = this.formatMoney(this.basePrice);
        breakdown.append(base);
        if (this.state.addonPrice) {
          const addon = document.createElement('small');
          addon.textContent = ` + ${this.formatMoney(this.state.addonPrice)} ${this.state.addonTitle}`;
          breakdown.append(addon);
        }
      }
    }

    continueFromAddress() {
      this.clearNotice();
      const selected = this.querySelector('input[name^="address-"]:checked');
      if (!selected) {
        this.showNotice('Please select a saved address or add a new address.');
        return;
      }

      if (selected.value === 'new') {
        const phoneInput = this.addressForm?.querySelector('[name="phone"]');
        if (phoneInput && this.state.phone) phoneInput.value = this.state.phone;
        this.setStep('address-form');
        this.addressForm?.querySelector('input')?.focus();
        return;
      }

      this.state.address = this.addressFromInput(selected);
      this.updateOrderReview();
      this.setStep('review');
    }

    handleAddressSubmit(event) {
      event.preventDefault();
      this.clearNotice();
      const form = event.currentTarget;
      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }

      const data = Object.fromEntries(new FormData(form).entries());
      const address = {
        id: `local-${Date.now()}`,
        firstName: String(data.firstName || '').trim(),
        lastName: String(data.lastName || '').trim(),
        email: String(data.email || '').trim(),
        address1: String(data.address1 || '').trim(),
        address2: String(data.address2 || '').trim(),
        landmark: String(data.landmark || '').trim(),
        locality: String(data.locality || '').trim(),
        city: String(data.city || '').trim(),
        province: String(data.province || '').trim(),
        country: 'India',
        zip: String(data.zip || '').trim(),
        phone: String(data.phone || '').trim()
      };

      this.state.address = address;
      this.saveLocalAddress(address);
      this.renderLocalAddresses(address.id);
      this.updateOrderReview();
      this.setStep('review');
    }

    addressFromInput(input) {
      return {
        id: input.value,
        firstName: input.dataset.firstName || '',
        lastName: input.dataset.lastName || '',
        email: input.dataset.email || this.dataset.customerEmail || '',
        address1: input.dataset.address1 || '',
        address2: input.dataset.address2 || '',
        landmark: input.dataset.landmark || '',
        locality: input.dataset.locality || '',
        city: input.dataset.city || '',
        province: input.dataset.province || '',
        country: input.dataset.country || 'India',
        zip: input.dataset.zip || '',
        phone: input.dataset.phone || ''
      };
    }

    getLocalAddresses() {
      try {
        const addresses = JSON.parse(localStorage.getItem(this.dataset.addressStorageKey) || '[]');
        return Array.isArray(addresses) ? addresses.slice(0, 8) : [];
      } catch (_error) {
        return [];
      }
    }

    saveLocalAddress(address) {
      const addresses = this.getLocalAddresses().filter((item) => {
        return !(item.address1 === address.address1 && item.zip === address.zip);
      });
      addresses.unshift(address);
      try {
        localStorage.setItem(this.dataset.addressStorageKey, JSON.stringify(addresses.slice(0, 8)));
      } catch (_error) {
        // Private browsing or storage limits should not interrupt checkout.
      }
    }

    renderLocalAddresses(selectedId = '') {
      const container = this.querySelector('[data-local-addresses]');
      if (!container) return;
      container.replaceChildren();

      this.getLocalAddresses().forEach((address) => {
        const label = document.createElement('label');
        label.className = 'puja-booking-option puja-booking-option--address';

        const input = document.createElement('input');
        input.type = 'radio';
        input.name = `address-${this.dataset.sectionId}`;
        input.value = address.id;
        input.dataset.savedAddress = '';
        Object.entries(address).forEach(([key, value]) => {
          if (key === 'id') return;
          input.dataset[key] = String(value || '');
        });
        input.checked = address.id === selectedId;

        const text = document.createElement('span');
        const title = document.createElement('strong');
        const detail = document.createElement('small');
        title.textContent = address.address1 || 'Saved address';
        detail.textContent = [address.address2, address.city, address.zip].filter(Boolean).join(', ');
        text.append(title, detail);
        label.append(input, text);
        container.append(label);
      });

      const savedCount = this.querySelectorAll('[data-saved-address]').length;
      const empty = this.querySelector('[data-address-empty]');
      if (empty) empty.hidden = savedCount > 0;
    }

    updateOrderReview() {
      const total = this.basePrice + this.state.addonPrice;
      const address = this.state.address || {};
      const addressText = [address.address1, address.address2, address.locality, address.city, address.province, address.zip]
        .filter(Boolean)
        .join(', ');

      this.setText('[data-review-address]', this.fullAddress(address) || addressText || '—');
      this.setText('[data-review-contact]', `${address.firstName || ''} ${address.lastName || ''}`.trim());
      this.setText('[data-review-email]', address.email);
      this.setText('[data-review-service-phone]', address.phone);
      this.updatePeopleSummary('review');
      this.setText('[data-review-date]', this.dateLabel);
      this.setText('[data-review-language]', this.state.language || '—');
      this.setText('[data-review-addon]', this.state.addonTitle || this.noAddonLabel);
      this.querySelectorAll('[data-review-subtotal]').forEach((element) => { element.textContent = this.formatMoney(total); });
      this.setText('[data-review-total]', this.formatMoney(total));
    }

    captureCoupon() {
      const input = this.querySelector('[data-coupon-code]');
      const code = input?.value.trim() || '';
      this.state.coupon = code;
      if (code) {
        this.showNotice('Your promo code will be checked and applied at Shopify checkout.', 'success');
      } else {
        this.showNotice('Enter a promo code first.');
        input?.focus();
      }
    }

    async payNow(button) {
      if (!this.state.address) {
        this.showNotice('Please select a service address before continuing.');
        return;
      }

      const variantId = Number(this.dataset.variantId || 0);
      if (!variantId) {
        this.showNotice('This Puja is not available for booking right now.');
        return;
      }

      this.clearNotice();
      button.disabled = true;
      button.classList.add('is-loading');

      const address = this.state.address;
      if (this.extendedBooking && (!this.capturePhone() || (this.state.bookingType !== 'Single' && !this.captureParticipants()))) {
        button.disabled = false;
        button.classList.remove('is-loading');
        return;
      }
      const properties = {
        'Booking': 'Puja service',
        'Muhurat preference': this.state.muhuratType === 'pandit' ? 'Local pandit to confirm date and time' : this.dateLabel,
        'Pandit language': this.state.language,
        'Selected add-on': this.state.addonTitle || this.noAddonLabel,
        'Service name': `${address.firstName || ''} ${address.lastName || ''}`.trim(),
        'Service email': address.email || '',
        'Service phone': address.phone || '',
        'Service address': this.fullAddress(address)
      };

      if (this.extendedBooking) {
        properties['Puja for'] = this.state.bookingType;
        properties['Number of persons'] = String(this.participantCount);
        properties['Booking phone'] = this.state.phone;
        properties['Booking subtotal'] = this.formatMoney(this.basePrice + this.state.addonPrice);
        this.state.participants.slice(0, this.participantCount).forEach((person, index) => {
          properties[`Person ${index + 1}`] = `${person.name} — Age: ${person.age} years`;
        });
      }
      const items = [{ id: variantId, quantity: 1, properties }];
      if (this.state.addonId) {
        items.push({
          id: this.state.addonId,
          quantity: 1,
          properties: {
            'Added with Puja': this.dataset.productTitle,
            'Puja for': this.state.bookingType,
            'Muhurat preference': this.dateLabel
          }
        });
      }

      try {
        const cartDrawer = this.dataset.completionMode === 'cart-drawer'
          ? document.querySelector('cart-drawer')
          : null;
        const requestBody = { items };
        if (cartDrawer?.getSectionsToRender) {
          requestBody.sections = cartDrawer.getSectionsToRender().map((section) => section.id);
          requestBody.sections_url = window.location.pathname;
          cartDrawer.setActiveElement?.(this.lastFocusedElement || button);
        }

        const response = await fetch(this.dataset.cartAddUrl || '/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(requestBody)
        });

        const result = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(result.description || result.message || 'The booking could not be added to cart.');

        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: result }));

        if (this.dataset.completionMode === 'cart-drawer') {
          this.closeModal();
          if (cartDrawer?.renderContents && result.sections) {
            cartDrawer.classList.remove('is-empty');
            cartDrawer.renderContents(result);
          } else {
            window.location.assign(window.routes?.cart_url || '/cart');
          }
          return;
        }

        const couponInput = this.querySelector('[data-coupon-code]');
        const coupon = (couponInput?.value || this.state.coupon || '').trim();
        const checkoutUrl = this.dataset.checkoutUrl || '/checkout';
        if (coupon) {
          window.location.assign(`/discount/${encodeURIComponent(coupon)}?redirect=${encodeURIComponent(checkoutUrl)}`);
        } else {
          window.location.assign(checkoutUrl);
        }
      } catch (error) {
        this.showNotice(error.message || 'Something went wrong. Please try again.');
        button.disabled = false;
        button.classList.remove('is-loading');
      }
    }

    handleKeydown(event) {
      if (!this.modal || this.modal.hidden) return;
      if (event.key === 'Escape') {
        this.closeModal();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = Array.from(this.dialog.querySelectorAll(
        'button:not([disabled]):not([hidden]), select:not([disabled]), input:not([disabled]):not([type="hidden"]), a[href], [tabindex]:not([tabindex="-1"])'
      )).filter((element) => !element.closest('[hidden]'));
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    setDateMinimum() {
      const input = this.querySelector('[data-booking-datetime]');
      if (!input) return;
      const nextSlot = new Date(Date.now() + 60 * 60 * 1000);
      nextSlot.setMinutes(Math.ceil(nextSlot.getMinutes() / 15) * 15, 0, 0);
      const offset = nextSlot.getTimezoneOffset() * 60000;
      input.min = new Date(nextSlot.getTime() - offset).toISOString().slice(0, 16);
    }

    formatMoney(cents) {
      const currency = this.dataset.currency || 'INR';
      try {
        return new Intl.NumberFormat('en-IN', {
          style: 'currency',
          currency,
          maximumFractionDigits: Number(cents) % 100 === 0 ? 0 : 2
        }).format(Number(cents || 0) / 100);
      } catch (_error) {
        return `₹${(Number(cents || 0) / 100).toFixed(2)}`;
      }
    }

    formatDate(value) {
      if (!value) return '';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return new Intl.DateTimeFormat('en-IN', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      }).format(date);
    }

    fullAddress(address) {
      return [address.address1, address.address2, address.landmark, address.locality, address.city, address.province, address.country, address.zip]
        .filter(Boolean)
        .join(', ');
    }

    showNotice(message, type = 'error') {
      if (!this.notice) return;
      this.notice.textContent = message;
      this.notice.hidden = false;
      this.notice.dataset.type = type;
      this.notice.style.borderColor = type === 'success' ? '#2f8c58' : '';
      this.notice.style.color = type === 'success' ? '#176b3c' : '';
      this.notice.style.background = type === 'success' ? '#effbf4' : '';
      this.notice.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }

    clearNotice() {
      if (!this.notice) return;
      this.notice.hidden = true;
      this.notice.textContent = '';
      this.notice.removeAttribute('data-type');
      this.notice.removeAttribute('style');
    }

    setText(selector, value) {
      const element = this.querySelector(selector);
      if (element) element.textContent = value || '—';
    }

    get extendedBooking() {
      return this.dataset.extendedBooking === 'true';
    }

    get participantCount() {
      return this.state.bookingType === 'Couple' ? 2 : this.state.bookingType === 'Family' ? this.state.familyCount : 1;
    }

    saveParticipantDraft() {
      this.querySelectorAll('[data-person-row]').forEach((row, index) => {
        this.state.participants[index] = {
          name: row.querySelector('[data-person-name]').value.trim(),
          age: row.querySelector('[data-person-age]').value
        };
      });
    }

    renderParticipants() {
      const container = this.querySelector('[data-participant-fields]');
      if (!container) return;
      container.replaceChildren();
      this.setText('[data-participant-type]', this.state.bookingType);
      const countWrap = this.querySelector('[data-family-count-wrap]');
      if (countWrap) countWrap.hidden = this.state.bookingType !== 'Family';
      if (this.state.bookingType === 'Single') return;
      for (let index = 0; index < this.participantCount; index += 1) {
        const person = this.state.participants[index] || { name: '', age: '' };
        const row = document.createElement('fieldset');
        row.className = 'puja-booking-person';
        row.dataset.personRow = '';
        const legend = document.createElement('legend');
        legend.textContent = `Person ${index + 1}`;
        row.append(legend);
        [['name', 'Full name', 'text'], ['age', 'Age (years)', 'number']].forEach(([key, title, type]) => {
          const label = document.createElement('label');
          const text = document.createElement('span');
          text.textContent = title;
          const input = document.createElement('input');
          input.type = type;
          input.required = true;
          input.className = 'puja-booking-field';
          input.dataset[key === 'name' ? 'personName' : 'personAge'] = '';
          input.value = person[key];
          input.name = `participant-${index + 1}-${key}`;
          if (key === 'age') {
            input.min = '0'; input.max = '120'; input.step = '1'; input.inputMode = 'numeric';
          } else {
            input.maxLength = 100; input.autocomplete = 'off';
          }
          label.append(text, input);
          row.append(label);
        });
        container.append(row);
      }
    }

    captureParticipants() {
      const count = this.querySelector('[data-family-count]');
      if (this.state.bookingType === 'Family' && (!count?.checkValidity() || Number(count.value) !== this.state.familyCount)) {
        this.showNotice('Enter a whole number of persons between 2 and 50.');
        count?.focus();
        return false;
      }
      const form = this.querySelector('[data-participants-form]');
      if (!form?.checkValidity()) {
        this.showNotice('Please enter a name and a valid age for every person.');
        form?.reportValidity();
        return false;
      }
      this.saveParticipantDraft();
      const people = this.state.participants.slice(0, this.participantCount);
      if (people.length !== this.participantCount || people.some((person) => !person.name)) {
        this.showNotice('Please enter a name for every person.');
        return false;
      }
      return true;
    }

    capturePhone() {
      const input = this.querySelector('[data-booking-phone]');
      const value = input?.value.trim() || '';
      const digits = value.replace(/\D/g, '');
      if (!/^\+?[\d\s()-]+$/.test(value) || (value.startsWith('+') ? digits.length < 8 || digits.length > 15 : !/^[6-9]\d{9}$/.test(digits))) {
        this.showNotice('Enter a valid 10-digit Indian mobile number, or an international number with + country code.');
        input?.focus();
        return false;
      }
      this.state.phone = value;
      return true;
    }

    handleExtraSubmit(event) {
      event.preventDefault();
      this.advanceTo(event.currentTarget.matches('[data-participants-form]') ? 'language' : 'cart');
    }

    updatePeopleSummary(prefix) {
      this.setText(`[data-${prefix}-type]`, this.state.bookingType);
      this.setText(`[data-${prefix}-count]`, String(this.participantCount));
      this.setText(`[data-${prefix}-phone]`, this.state.phone);
      const row = this.querySelector(`[data-${prefix}-participants-row]`);
      const list = this.querySelector(`[data-${prefix}-participants]`);
      if (row) row.hidden = this.state.bookingType === 'Single';
      if (!list) return;
      list.replaceChildren();
      if (this.state.bookingType === 'Single') return;
      this.state.participants.slice(0, this.participantCount).forEach((person) => {
        const item = document.createElement('li');
        item.textContent = `${person.name} — ${person.age} years`;
        list.append(item);
      });
    }

    get basePrice() {
      return Number(this.dataset.variantPrice || 0);
    }

    get dateLabel() {
      return this.state.muhuratType === 'pandit'
        ? 'Local pandit will confirm the Muhurat'
        : this.formatDate(this.state.dateTime);
    }

    get noAddonLabel() {
      return this.querySelector('input[name^="addon-"][value="none"]')?.nextElementSibling?.textContent?.trim() || 'No add-on';
    }
  }

  customElements.define('puja-booking-detail', PujaBookingDetail);
}
